import { AnalyticsPlugins } from '@/modules/analytics';
import { useToast } from '@chakra-ui/react';
import axios, { AxiosError } from 'axios';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { SWRConfig } from 'swr';
import { useAnalytics } from 'use-analytics';
import { buildError, ErrorMessage } from './build-error';

const toastDuration = 10000;
const defaultToastParams = {
  isClosable: true,
  duration: toastDuration,
};

export default function NetworkErrorBoundary({ children }: PropsWithChildren) {
  const toast = useToast();
  const analytics = useAnalytics();
  const [axiosErrors, setAxiosErrors] = useState<Map<string, ErrorMessage>>(new Map());

  const analyticsPlugins = analytics.plugins as unknown as AnalyticsPlugins;

  useEffect(() => {
    axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        console.error(error);

        const builtError = buildError(error);

        const currentErrors = new Map(axiosErrors);
        currentErrors.set(builtError.id, builtError);
        setAxiosErrors(currentErrors);

        setTimeout(() => {
          currentErrors.delete(builtError.id);
          setAxiosErrors(currentErrors);
        }, toastDuration);

        return Promise.reject(error);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {axiosErrors.size > 0 &&
        Array.from(axiosErrors).map((err) => {
          const [id, errorMessage] = err;

          if (!toast.isActive(id)) {
            return toast({
              id,
              title: errorMessage.errorMessage,
              status: 'error',
              ...defaultToastParams,
            });
          }

          return null;
        })}

      <SWRConfig
        value={{
          onError: (error: AxiosError) => {
            console.error(error);
            const builtError = buildError(error);

            if (!toast.isActive(builtError.id)) {
              toast({
                id: builtError.id,
                title: builtError.errorMessage,
                status: 'error',
                ...defaultToastParams,
              });
            }

            if (analyticsPlugins?.datadogRum?.error) {
              analyticsPlugins.datadogRum.error(error);
            }

            throw error;
          },
        }}
      >
        {children}
      </SWRConfig>
    </>
  );
}
